/* eslint-disable max-len */
/**
 * CycleVisualization Component
 * 
 * A high-performance timeline visualization for billing cycles.
 * Features include zoom controls, row visibility toggles, and interactive cycle details.
 * 
 * @component
 * @example
 * ```tsx
 * <CycleVisualization
 *   selectedLines={contractLines}
 *   cyclesPerLine={5}
 *   statusColors={{
 *     Excluded: "bg-gray-400",
 *     SoCreated: "bg-green-500"
 *   }}
 * />
 * ```
 * 
 * @props
 * - selectedLines: ContractLine[] - Array of contract lines with billing cycles
 * - cyclesPerLine?: number - Optional limit on cycles to display per line
 * - statusColors?: Record<string, string> - Optional custom status color mappings
 * - onCycleClick?: (cycle: BillingCycle) => void - Optional callback when cycle is clicked
 * - dateFormat?: string - Optional date format string (default: "MMM d, yyyy")
 * 
 * @performance
 * - Optimized with useMemo for expensive calculations
 * - Virtualization-ready structure for large datasets
 * - Efficient re-render prevention with Set-based state
 * - Single popover management to prevent memory leaks
 */

import { useMemo, useState, useCallback } from 'react';

import { Eye, EyeOff, ZoomIn, ZoomOut, ExternalLink, Bookmark, Pause } from 'lucide-react';

import { ContractLine, BillingCycle } from './types';
import { SalesOrderStatusEnum } from '@/graphql/contracts/_generated_/contracts';
import { UserRole } from '@/config/roles.config';
import { browserHistory } from '@/routing/browserHistory';
import { getParameterizedLink } from '@/utils/getLinkWithSubscriptionId.utils';
import { billingBCPath } from '@/utils/path.utlis';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/evo/ui/card';
import { Badge } from '@/components/evo/ui/badge';
import { Button } from '@/components/evo/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/evo/ui/popover';
import { CycleActionButtons } from './CycleActionButtons';
import { CONTRACT_STATES_COLORS } from '@/config/statuses.config';

interface CycleVisualizationProps {
  selectedLines: ContractLine[];
  cyclesPerLine?: number;
  takeFromStart?: boolean;
  statusColors?: Record<string, string>;
  onCycleClick?: (cycle: BillingCycle) => void;
  dateFormat?: string;
  // Lifted state props for preserving state when switching tabs
  hiddenRows?: Set<number>;
  onHiddenRowsChange?: (hiddenRows: Set<number>) => void;
  zoomLevel?: number;
  onZoomLevelChange?: (zoomLevel: number) => void;
  openPopovers?: Set<string>;
  onOpenPopoversChange?: (openPopovers: Set<string>) => void;
  // Callback for data changes (mutations) to trigger refetch
  onDataChanged?: () => void;
  // Callback for opening the invoice modal (lifted to parent)
  onInvoiceCycle?: (cycle: BillingCycle) => void;
  userRole?: UserRole;
  // Bookmark state props (lifted to parent so it resets when modal closes)
  bookmarkedCycles?: Set<string>;
  onBookmarkedCyclesChange?: (bookmarkedCycles: Set<string>) => void;
  readOnlyLineIds?: Set<string>;
}

const PAUSE_VISIBLE_STATUSES = ['Active', 'ActiveDNR', 'Terminating'];

interface TimelineData {
  stockCode: string;
  stockName: string;
  status: string;
  lineBillingStatus?: string | null;
  lineBillingCurrentStatusStartDate?: string | null;
  lineBillingCurrentStatusEndDate?: string | null;
  cycles: BillingCycle[];
}

// Default status color mappings - can be overridden via props
const DEFAULT_STATUS_COLORS: Record<string, string> = {
  Excluded: 'bg-gray-400 dark:bg-gray-600',
  SoCreated: 'bg-green-500 dark:bg-green-600',
  ActivePending: 'bg-blue-500 dark:bg-blue-600',
  Cancelled: 'bg-red-500 dark:bg-red-600',
  Terminated: 'bg-orange-500 dark:bg-orange-600',
  Merged: 'bg-amber-700 dark:bg-amber-800',
};

// Zoom configuration
const ZOOM_MIN = 100;
const ZOOM_MAX = 400;
const ZOOM_STEP = 25;

export function CycleVisualization({
  selectedLines,
  cyclesPerLine,
  takeFromStart = false,
  statusColors = DEFAULT_STATUS_COLORS,
  onCycleClick,
  hiddenRows: externalHiddenRows,
  onHiddenRowsChange,
  zoomLevel: externalZoomLevel,
  onZoomLevelChange,
  openPopovers: externalOpenPopovers,
  onOpenPopoversChange,
  onDataChanged,
  onInvoiceCycle,
  userRole,
  bookmarkedCycles: externalBookmarkedCycles,
  onBookmarkedCyclesChange,
}: CycleVisualizationProps) {
  // State: Row visibility - use external state if provided, otherwise local state
  const [internalHiddenRows, setInternalHiddenRows] = useState<Set<number>>(new Set());
  const hiddenRows = externalHiddenRows ?? internalHiddenRows;
  const setHiddenRows = onHiddenRowsChange ?? setInternalHiddenRows;

  // State: Zoom level - use external state if provided, otherwise local state
  const [internalZoomLevel, setInternalZoomLevel] = useState<number>(ZOOM_MIN);
  const zoomLevel = externalZoomLevel ?? internalZoomLevel;
  const setZoomLevel = onZoomLevelChange ?? setInternalZoomLevel;

  // State: Popover visibility - use external state if provided, otherwise local state
  const [internalOpenPopovers, setInternalOpenPopovers] = useState<Set<string>>(new Set());
  const openPopovers = externalOpenPopovers ?? internalOpenPopovers;
  const setOpenPopovers = onOpenPopoversChange ?? setInternalOpenPopovers;

  // State: Bookmarked cycles - use external state if provided, otherwise local state
  const [internalBookmarkedCycles, setInternalBookmarkedCycles] = useState<Set<string>>(new Set());
  const bookmarkedCycles = externalBookmarkedCycles ?? internalBookmarkedCycles;
  const setBookmarkedCycles = onBookmarkedCyclesChange ?? setInternalBookmarkedCycles;

  // Memoized timeline data transformation
  const timelineData = useMemo<TimelineData[]>(() => {
    return selectedLines.map((line) => {
      let cycles: BillingCycle[];
      if (cyclesPerLine) {
        // Take first N or last N cycles based on takeFromStart
        cycles = takeFromStart
          ? line.BillingCycles.slice(0, cyclesPerLine)
          : line.BillingCycles.slice(-cyclesPerLine);
      } else {
        cycles = line.BillingCycles;
      }

      return {
        stockCode: line.stockCode,
        stockName: line.stockName,
        status: line.status,
        lineBillingStatus: (line as Record<string, unknown>).lineBillingStatus as string | null | undefined,
        lineBillingCurrentStatusStartDate: (line as Record<string, unknown>).lineBillingCurrentStatusStartDate as string | null | undefined,
        lineBillingCurrentStatusEndDate: (line as Record<string, unknown>).lineBillingCurrentStatusEndDate as string | null | undefined,
        cycles,
      };
    });
  }, [selectedLines, cyclesPerLine, takeFromStart]);

  // Filter out hidden rows for calculations
  const visibleTimelineData = useMemo(() => {
    return timelineData.filter((_, index) => !hiddenRows.has(index));
  }, [timelineData, hiddenRows]);

  // Get all unique dates for x-axis from VISIBLE rows only
  const visibleDates = useMemo(() => {
    const dates = new Set<string>();
    visibleTimelineData.forEach((data) => {
      data.cycles.forEach((cycle) => {
        dates.add(cycle.startDate);
        dates.add(cycle.endDate);
      });
    });
    return Array.from(dates).sort();
  }, [visibleTimelineData]);

  // Calculate date range from VISIBLE rows only
  const visibleDateRange = useMemo(() => {
    if (visibleDates.length === 0) return { min: new Date(), max: new Date() };
    return {
      min: new Date(visibleDates[0]),
      max: new Date(visibleDates[visibleDates.length - 1]),
    };
  }, [visibleDates]);

  // Calculate overall start and end dates from VISIBLE cycles only
  const overallDateRange = useMemo<{ startDate: Date | null; endDate: Date | null }>(() => {
    let minStartDate: Date | null = null;
    let maxEndDate: Date | null = null;

    visibleTimelineData.forEach((data) => {
      data.cycles.forEach((cycle) => {
        const startDate = new Date(cycle.startDate);
        const endDate = new Date(cycle.endDate);

        if (!minStartDate || startDate < minStartDate) {
          minStartDate = startDate;
        }
        if (!maxEndDate || endDate > maxEndDate) {
          maxEndDate = endDate;
        }
      });
    });

    return {
      startDate: minStartDate,
      endDate: maxEndDate,
    };
  }, [visibleTimelineData]);

  // Get unique statuses for legend
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    timelineData.forEach((data) => {
      data.cycles.forEach((cycle) => {
        if (cycle.processingStatus) {
          statuses.add(cycle.processingStatus);
        }
      });
    });
    return Array.from(statuses);
  }, [timelineData]);

  // Optimized handlers with useCallback
  const toggleRowVisibility = useCallback((index: number) => {
    const newSet = new Set(hiddenRows);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setHiddenRows(newSet);
  }, [hiddenRows, setHiddenRows]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel(Math.min(zoomLevel + ZOOM_STEP, ZOOM_MAX));
  }, [zoomLevel, setZoomLevel]);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(Math.max(zoomLevel - ZOOM_STEP, ZOOM_MIN));
  }, [zoomLevel, setZoomLevel]);

  const toggleBookmark = useCallback((popoverId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSet = new Set(bookmarkedCycles);
    if (newSet.has(popoverId)) {
      newSet.delete(popoverId);
    } else {
      newSet.add(popoverId);
    }
    setBookmarkedCycles(newSet);
  }, [bookmarkedCycles, setBookmarkedCycles]);

  const handlePopoverChange = useCallback((popoverId: string, isOpen: boolean) => {
    const newSet = new Set(openPopovers);
    if (isOpen) {
      // Close all other popovers and open this one
      newSet.clear();
      newSet.add(popoverId);
    } else {
      newSet.delete(popoverId);
    }
    setOpenPopovers(newSet);
  }, [openPopovers, setOpenPopovers]);

  // Utility functions
  const formatDate = useCallback((dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const getPositionPercentage = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const range = visibleDateRange.max.getTime() - visibleDateRange.min.getTime();
    const position = date.getTime() - visibleDateRange.min.getTime();
    return (position / range) * 100;
  }, [visibleDateRange]);

  const isCycleInPausedPeriod = useCallback((
    cycle: BillingCycle,
    lineBillingStatus: string | null | undefined,
    pauseStart: string | null | undefined,
    pauseEnd: string | null | undefined,
  ): boolean => {
    if (cycle.salesOrderStatus === SalesOrderStatusEnum.BILLING_PAUSED) return true;
    if (!lineBillingStatus || (lineBillingStatus !== 'Paused' && lineBillingStatus !== 'FuturePause')) return false;
    if (!pauseStart || !cycle.billingDate) return false;
    const billingDate = new Date(cycle.billingDate).getTime();
    const pStart = new Date(pauseStart).getTime();
    const pEnd = pauseEnd ? new Date(pauseEnd).getTime() : Infinity;
    return billingDate >= pStart && billingDate <= pEnd;
  }, []);

  // Early return for empty state
  if (selectedLines.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No contract lines selected
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Billing Cycle Timeline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Fixed Header Section: Legend with Zoom Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b bg-background">
          <div className="flex flex-wrap gap-4">
            {uniqueStatuses.map((status) => (
              <div key={status} className="flex items-center gap-2">
                <div
                  className={`w-4 h-4 rounded ${statusColors[status] || 'bg-gray-400'}`}
                />
                <span className="text-sm">{status}</span>
              </div>
            ))}
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground mr-1">Zoom:</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomOut}
              disabled={zoomLevel <= ZOOM_MIN}
              className="h-8 w-8 p-0"
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-xs font-medium min-w-[3rem] text-center">
              {zoomLevel}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomIn}
              disabled={zoomLevel >= ZOOM_MAX}
              className="h-8 w-8 p-0"
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Date Range - Below Legend and Delimiter */}
        <div className="flex items-center justify-between text-sm font-medium text-muted-foreground pb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs">Start:</span>
            <span>
              {overallDateRange.startDate
                ? overallDateRange.startDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
                : 'N/A'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs">End:</span>
            <span>
              {overallDateRange.endDate
                ? overallDateRange.endDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
                : 'N/A'}
            </span>
          </div>
        </div>

        {/* Scrollable Content Area: Timeline Chart */}
        <div className="max-h-[500px] overflow-y-auto overflow-x-auto space-y-8 pr-2 relative">
          {/* Inner container for zoom - applies width scaling */}
          <div style={{ minWidth: `${zoomLevel}%` }}>
            {timelineData.map((data, lineIndex) => {
              const isHidden = hiddenRows.has(lineIndex);

              return (
                <div key={lineIndex} className={`space-y-2 ${isHidden ? 'mb-2' : 'mb-8'}`}>
                  {/* Stock Code and Name with Status Badge and Hide/Show Button */}
                  <div className="flex items-center gap-2">
                    {/* Hide/Show Toggle Button - Leftmost Position */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRowVisibility(lineIndex)}
                      className="h-auto px-2 py-1 hover:bg-muted flex-shrink-0"
                      title={isHidden ? 'Unhide timeline' : 'Hide timeline'}
                    >
                      {isHidden ? (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {data.stockCode} - {data.stockName}
                      </span>
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
                        style={{ backgroundColor: CONTRACT_STATES_COLORS[data.status] || '#73808b' }}
                      >
                        {data.status}
                      </span>
                      {PAUSE_VISIBLE_STATUSES.includes(data.status) && (data.lineBillingStatus === 'Paused' || data.lineBillingStatus === 'FuturePause') && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-white cursor-default"
                          style={{ backgroundColor: data.lineBillingStatus === 'Paused' ? '#9C27B0' : '#00BCD4' }}
                          title={`${data.lineBillingStatus === 'Paused' ? 'Paused' : 'Future Pause'}\nStart Date: ${data.lineBillingCurrentStatusStartDate ? new Date(data.lineBillingCurrentStatusStartDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}\nEnd Date: ${data.lineBillingCurrentStatusEndDate ? new Date(data.lineBillingCurrentStatusEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}`}
                        >
                          <Pause className="h-3 w-3" />
                          {data.lineBillingStatus === 'Paused' ? 'Billing Paused' : 'Future Pause'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Timeline or Empty State - Only show if not hidden */}
                  {!isHidden && (
                    <>
                      {data.cycles.length === 0 ? (
                        <div className="relative h-12 bg-muted/30 rounded-md flex items-center justify-center">
                          <span className="text-sm text-muted-foreground">Line has no cycles</span>
                        </div>
                      ) : (
                        <div className="relative h-12 bg-muted/30 rounded-md">
                          {data.cycles.map((cycle, cycleIndex) => {
                            const startPos = getPositionPercentage(cycle.startDate);
                            const endPos = getPositionPercentage(cycle.endDate);
                            const width = endPos - startPos;
                            const popoverId = `${lineIndex}-${cycleIndex}`;
                            const isPopoverOpen = openPopovers.has(popoverId);
                            const cyclePaused = isCycleInPausedPeriod(
                              cycle,
                              data.lineBillingStatus,
                              data.lineBillingCurrentStatusStartDate,
                              data.lineBillingCurrentStatusEndDate,
                            );

                            return (
                              <Popover
                                key={cycleIndex}
                                open={isPopoverOpen}
                                onOpenChange={(isOpen) => handlePopoverChange(popoverId, isOpen)}
                              >
                                <PopoverTrigger asChild>
                                  <div
                                    className={`absolute top-1/2 -translate-y-1/2 h-8 rounded
                                      cursor-pointer transition-all hover:h-10 ${isPopoverOpen ? 'ring-2 ring-white dark:ring-gray-800' : ''
                                      } ${(cycle.processingStatus && statusColors[cycle.processingStatus]) || 'bg-gray-400'
                                      }`}
                                    style={{
                                      left: `${startPos}%`,
                                      width: `${width}%`,
                                    }}
                                    onClick={() => onCycleClick?.(cycle)}
                                  >
                                    {(bookmarkedCycles.has(popoverId) || cyclePaused) && (
                                      <div className="absolute inset-0 flex items-center justify-center gap-0.5 pointer-events-none">
                                        {bookmarkedCycles.has(popoverId) && (
                                          <Bookmark className="h-4 w-4 fill-white text-white drop-shadow-md" />
                                        )}
                                        {cyclePaused && (
                                          <Pause className="h-4 w-4 fill-white text-white drop-shadow-md" />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto p-3 text-xs"
                                  side="top"
                                  align="center"
                                  sideOffset={8}
                                  collisionPadding={10}
                                >
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="font-semibold">{cycle.processingStatus}</span>
                                      <button
                                        type="button"
                                        onClick={(e) => toggleBookmark(popoverId, e)}
                                        className={`inline-flex items-center justify-center rounded p-0.5 transition-colors hover:bg-muted ${bookmarkedCycles.has(popoverId)
                                          ? 'text-primary'
                                          : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                        title={bookmarkedCycles.has(popoverId) ? 'Remove bookmark' : 'Bookmark as verified'}
                                      >
                                        <Bookmark
                                          className={`h-3.5 w-3.5 ${bookmarkedCycles.has(popoverId) ? 'fill-primary' : ''
                                          }`}
                                        />
                                      </button>
                                    </div>
                                    {cycle.statusReason && (
                                      <div className="text-muted-foreground">{cycle.statusReason}</div>
                                    )}
                                    <div className="pt-2 space-y-0.5 border-t">
                                      <div>BC Start: {formatDate(cycle.startDate)}</div>
                                      <div>BC End: {formatDate(cycle.endDate)}</div>
                                      <div>Billing: {formatDate(cycle.billingDate)}</div>
                                      <div>
                                        Value: {cycle.currency} {cycle.billingValue.toLocaleString(
                                        'en-US',
                                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                                      )}
                                      </div>
                                      {/* SO Count for SoCreated cycles */}
                                      {cycle.processingStatus === 'SoCreated'
                                        && cycle.soCount != null && cycle.soCount > 0 && (
                                          <div className="flex items-center gap-1">
                                            <span>SO Count: {cycle.soCount}</span>
                                            <button
                                              type="button"
                                              className="inline-flex items-center hover:text-primary"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const link = `/${billingBCPath}?billingCycleId=${cycle.billingCycleID}`;
                                                browserHistory.push(getParameterizedLink(link));
                                              }}
                                              title="View Sales Orders"
                                            >
                                              <ExternalLink className="h-3 w-3" />
                                            </button>
                                          </div>
                                      )}
                                    </div>

                                    <CycleActionButtons cycle={cycle} variant="default" onDataChanged={onDataChanged} onInvoice={onInvoiceCycle} userRole={userRole} />

                                  </div>
                                </PopoverContent>
                              </Popover>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        <div className="pt-4 border-t">
          <div className="flex flex-wrap gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total Lines:</span>{' '}
              <Badge variant="secondary">{timelineData.length}</Badge>
            </div>
            <div>
              <span className="text-muted-foreground">Total Cycles:</span>{' '}
              <Badge variant="secondary">
                {timelineData.reduce((sum, data) => sum + data.cycles.length, 0)}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
